/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

/**
 * Search param set by the cart/PDP PayPal express flow when the PayPal-returned shipping ZIP does
 * not match the shopper's persisted delivery ZIP. Ashley pricing/inventory is ZIP-driven, so the
 * shopper is routed to checkout to confirm the correct shipping address before placing the order.
 */
export const PAYPAL_ZIP_ERROR_PARAM = 'paypalZipError';

/**
 * Dismissible notice shown at the top of checkout after a PayPal express ZIP mismatch.
 *
 * Legacy parity: the SFRA flow set `session.custom.zipError` and rendered the `paypalAddressChangeNotice`
 * banner at the top of the COShipping-Start page (heading + two message lines + a close affordance —
 * not a modal, no "change ZIP" button). This is the Storefront Next equivalent.
 *
 * Visibility is derived directly from the `paypalZipError` search param so it is robust against the
 * remounts that happen while the checkout page hydrates (cart provider flips skeleton ↔ content). We
 * deliberately do NOT strip the param on mount: doing so triggers a React Router revalidation during
 * that unstable window, which remounts this component, which then reads the already-stripped URL and
 * hides itself — the notice flashes and vanishes. Instead the param is only removed on explicit
 * dismiss (via `setSearchParams`, so React Router's own state stays in sync and a post-dismiss remount
 * does not resurface it).
 *
 * Visual styling mirrors the existing checkout message card (CheckoutErrorBanner).
 */
export default function PayPalZipErrorNotice() {
    const { t } = useTranslation('checkout');
    const [searchParams, setSearchParams] = useSearchParams();
    const [dismissed, setDismissed] = useState(false);
    const visible = !dismissed && searchParams.has(PAYPAL_ZIP_ERROR_PARAM);

    const handleDismiss = useCallback(() => {
        setDismissed(true);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete(PAYPAL_ZIP_ERROR_PARAM);
                return next;
            },
            { replace: true, preventScrollReset: true }
        );
    }, [setSearchParams]);

    if (!visible) {
        return null;
    }

    return (
        <div
            role="alert"
            aria-live="polite"
            data-testid="paypal-zip-error-notice"
            className={cn(
                'relative flex items-start gap-3 rounded border border-destructive/20 bg-destructive/10 px-4 py-3 text-destructive'
            )}>
            <div className="flex-1 pr-6">
                <p className="text-xl font-bold">{t('payPalZipError.header')}</p>
                <p className="mt-1 text-sm">{t('payPalZipError.message')}</p>
                <p className="mt-1 text-sm">{t('payPalZipError.secondaryMessage')}</p>
            </div>
            <button
                type="button"
                onClick={handleDismiss}
                aria-label={t('payPalZipError.dismiss')}
                className="absolute right-2 top-2 rounded p-1 text-destructive/70 transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/40">
                <X className="size-4" aria-hidden="true" />
            </button>
        </div>
    );
}
